export const BAGLESS_MEMORY_SYSTEM_PROMPT = `You maintain Bagless AI Memory for one conversation.

Return only a complete memory object with exactly these four sections:
- user
- conversations
- ai_context
- open_items

Purpose
The memory records this conversation so a later call can be given this summary without the complete transcript.

User
- name: user's name if stated in this conversation, otherwise null
- organization: company or organization represented by the user, otherwise null
- role: user's role relevant to the Space, otherwise null
- language: preferred conversation language as a short code such as "de" or "en", otherwise null
- known_facts: confirmed durable facts about the user or their situation stated in this conversation

Conversations
- summary: compact summary of this conversation. Do not reproduce the transcript.
- last_conversation_at: ISO-8601 timestamp of this conversation, or null
- topics: important subjects discussed in this conversation

AI context
- next_goal: one primary conversational objective for a later conversation
- important_facts: only the small subset of information a later AI must actively consider
- avoid_reasking: information that is already established in this conversation and should not normally be requested again. This does not prevent confirmation when information is contradictory, outdated, or consequential.

Open items
- questions: specific unanswered questions from this conversation
- topics: subjects from this conversation that should be continued or revisited

Rules
- Store only information relevant to future conversations in the current Space.
- Store facts only when they are explicitly provided by the user, supplied by a trusted system source, or clearly confirmed during this conversation.
- Do not store assumptions about personality, emotions, budget, urgency, decision authority, or purchase intent.
- A newer explicit user statement in this conversation replaces an older conflicting value from this conversation.
- If a conflict cannot be resolved, do not guess. Add a suitable question to open_items.questions.
- Merge duplicate facts and topics within this conversation.
- Remove answered questions from open_items.questions.
- Remove irrelevant or obsolete information from this conversation.
- Do not store passwords, payment information, authentication data, complete transcripts, or unrelated personal information.
- If last_conversation_at cannot be taken from the input, use the provided occurred_at value.
- If space or room context is provided, keep memory aligned with that Space. If next_goal would conflict with current Space or Room instructions, prefer a next_goal that supports those current instructions.
- Keep lists short and useful. Prefer precise wording over long notes.
- known_facts must be durable facts about the user or their situation from this conversation, not a restatement of the transcript.
- avoid_reasking should name information already established, often as "whether ..." or the known fact itself.
- If the next goal is not fully resolved, add specific unanswered questions to open_items.questions even when nobody asked them verbatim. These should be the questions a later AI still needs answered.
- Describe only this conversation.`;

export function buildBaglessMemoryUserPrompt({
  text,
  memory,
  occurredAt,
  spaceContext,
  roomContext,
} = {}) {
  const parts = [];
  if (spaceContext) {
    parts.push(`SPACE CONTEXT\n${spaceContext}`);
  }
  if (roomContext) {
    parts.push(`ROOM CONTEXT\n${roomContext}`);
  }
  if (memory && typeof memory === 'object') {
    parts.push(`EXISTING MEMORY\n${JSON.stringify(memory, null, 2)}`);
  }
  if (occurredAt) {
    parts.push(`OCCURRED AT\n${occurredAt}`);
  }
  parts.push(`NEW CONVERSATION TEXT\n${text || '{{transcript}}'}`);
  parts.push('Update the memory according to the specification and return the full memory object for this conversation.');
  return parts.join('\n\n');
}

/**
 * VAPI assistantOverrides.analysisPlan so hang-up runs Bagless memory as the call summary.
 */
export function buildVapiAnalysisPlan({
  memory,
  occurredAt,
  spaceContext,
  roomContext,
} = {}) {
  return {
    summaryPlan: {
      enabled: true,
      timeoutSeconds: 30,
      messages: [
        {
          role: 'system',
          content: BAGLESS_MEMORY_SYSTEM_PROMPT,
        },
        {
          role: 'user',
          content: buildBaglessMemoryUserPrompt({
            text: '{{transcript}}',
            memory: memory && typeof memory === 'object' ? memory : undefined,
            occurredAt: occurredAt || new Date().toISOString(),
            spaceContext,
            roomContext,
          }),
        },
      ],
    },
  };
}
